/**
 * Chi può fare cosa, e quali gesti dichiarano **chi** li ha fatti.
 *
 * Sta nel dominio e non nell'infrastruttura per la stessa ragione delle altre
 * regole: è una decisione di reparto, non un dettaglio tecnico. La password, il
 * token e l'hash sono infrastruttura e stanno in `apps/api/src/auth`; *quale
 * ruolo può marcare una cassetta pronta* è una regola, e come tutte le regole
 * deve poter essere letta e verificata senza montare niente.
 *
 * ## Le due domande, che sono diverse
 *
 * `puo()` chiede **se ti è permesso**. `richiedeConferma()` chiede se il gesto
 * pretende che tu **sia davvero tu, adesso**.
 *
 * La seconda esiste per un guasto preciso. Le postazioni sono condivise e si
 * entra con utente e password a inizio turno: se nient'altro chiedesse
 * l'identità, dopo due settimane ci sarebbe una sessione unica aperta tutto il
 * giorno, e le ore misurate dai timer finirebbero tutte sulla prima persona che
 * ha fatto login. La dashboard di capacità misurerebbe una cosa che non è
 * successa, e il lavoro fatto per i timer sarebbe sprecato.
 *
 * Quindi i gesti che **scrivono un nome** — aprire una lavorazione col timer,
 * dichiarare un capo rovinato, autorizzare una spedizione parziale — chiedono
 * di nuovo la password. Gli altri no: un bip in più su ogni capo contato è
 * esattamente il tipo di attrito che fa aggirare il sistema.
 */
export const ROLES = [
    'ricezione',
    'produzione',
    'magazzino',
    'fine-linea',
    'amministratore',
];
export function isRole(v) {
    return ROLES.includes(v);
}
const PERMESSI = {
    'ricezione.aggancia-capo': { ruoli: ['ricezione'], attribuito: false },
    'ricezione.apri-cassetta': { ruoli: ['ricezione'], attribuito: false },
    'ricezione.conta-capo': { ruoli: ['ricezione'], attribuito: false },
    'ricezione.togli-capo': { ruoli: ['ricezione'], attribuito: false },
    'ricezione.svuota-cassetta': { ruoli: ['ricezione'], attribuito: false },
    'ricezione.chiudi-arrivo': {
        ruoli: ['ricezione'],
        attribuito: true,
        perche: 'scrive su DecoNetwork e crea una missione a nome di qualcuno',
    },
    'ricezione.segna-fallato': {
        ruoli: ['ricezione'],
        attribuito: true,
        perche: "parte una mail all'ufficio acquisti a nome di qualcuno, e misura il fornitore",
    },
    /*
     * Non attribuito, benché la riga porti chi ha scattato.
     *
     * Il criterio scritto in testa a questo file è «il gesto scrive un nome» nel
     * senso di *misura una persona, o parla a nome sua a qualcuno*. Qui il nome è
     * provenienza su un dato tecnico: nessun timer, nessun costo e nessuna mail
     * pendono da lui. E il costo opposto è quello che il criterio nomina da sé —
     * la foto si chiede su **ogni riga neutra**, e una password a ogni scatto è
     * precisamente l'attrito che fa aggirare il sistema. Il risultato non sarebbe
     * un archivio più tracciato: sarebbe un archivio vuoto.
     *
     * Il fallato resta attribuito, e la foto che gli si attacca eredita
     * l'attribuzione dal gesto giusto.
     */
    'ricezione.fotografa-capo': { ruoli: ['ricezione'], attribuito: false },
    'ricezione.stampa-etichette': { ruoli: ['ricezione'], attribuito: false },
    'ricezione.conferma-contenuto': { ruoli: ['ricezione'], attribuito: false },
    'ricezione.marca-pronte': { ruoli: ['ricezione'], attribuito: false },
    /*
     * Del magazzino, e non del banco che pure stampa etichette tutto il giorno.
     *
     * Quelle del banco descrivono la merce che sta contando; questa descrive il
     * contenitore, e i contenitori sono del magazzino — è lui che li conta, li
     * ritira quando si rompono e sa quali numeri sono in giro.
     */
    'attrezzatura.stampa-etichette': { ruoli: ['magazzino'], attribuito: false },
    // The warehouse reads the registry because its label screen picks from it.
    // Changing it is the administrator's: a renamed crate is a relabelled crate.
    'attrezzatura.vedi': { ruoli: ['magazzino'], attribuito: false },
    'attrezzatura.gestisci': { ruoli: [], attribuito: false },
    'magazzino.conferma-carico': { ruoli: ['magazzino'], attribuito: false },
    'magazzino.chiama-prelievo': { ruoli: ['magazzino'], attribuito: false },
    // Guardare una coda non scrive il nome di nessuno: leggere non è mai
    // attribuito, ed è la ragione per cui esiste un gesto di lettura separato
    // invece di riusare quello che apre la lavorazione.
    'produzione.vedi': { ruoli: ['produzione'], attribuito: false },
    /*
     * Vedere e scaricare gli esecutivi archiviati.
     *
     * Non attribuito: guardare non misura nessuno e non parla a nome di nessuno.
     * Ma **non è pubblico**, e la differenza con le foto della merce è netta —
     * quelle le consuma un `<img>` che non manda l'`Authorization`, qui c'è una
     * schermata che il token ce l'ha. E un esecutivo è il disegno del cliente:
     * la proprietà intellettuale di qualcun altro, non una foto di uno scaffale.
     */
    'esecutivi.vedi': { ruoli: ['produzione'], attribuito: false },
    'produzione.apri-lavorazione': {
        ruoli: ['produzione'],
        attribuito: true,
        perche: 'fa partire il timer, e il tempo finisce nelle ore allocate a una persona',
    },
    'produzione.chiudi-lavorazione': {
        ruoli: ['produzione'],
        attribuito: true,
        perche: 'chiude il timer: senza identità fresca il tempo è attribuito a caso',
    },
    'produzione.capo-rovinato': {
        ruoli: ['produzione'],
        attribuito: true,
        perche: "lo scarto di lavorazione misura noi, non il fornitore: serve sapere chi c'era",
    },
    'fine-linea.apri-collo': { ruoli: ['fine-linea'], attribuito: false },
    'fine-linea.chiudi-collo': { ruoli: ['fine-linea'], attribuito: false },
    'fine-linea.chiudi-spedizione': {
        ruoli: ['fine-linea'],
        attribuito: true,
        perche: "da qui parte merce verso un cliente, ed è l'ultimo punto in cui si poteva fermare",
    },
    'fine-linea.annulla-spedizione': {
        ruoli: ['fine-linea'],
        attribuito: true,
        perche: "la merce era pronta a partire e qualcuno l'ha fermata",
    },
    'fine-linea.peso-manuale': {
        ruoli: ['fine-linea'],
        attribuito: true,
        perche: "il peso va in fattura e non l'ha misurato la macchina",
    },
    'fine-linea.correggi-indirizzo': {
        ruoli: ['fine-linea'],
        attribuito: true,
        perche: "l'indirizzo sull'etichetta è quello che ha scritto chi lo ha corretto",
    },
    'fine-linea.riprova-spedizione': { ruoli: ['fine-linea'], attribuito: false },
    'fine-linea.etichetta': { ruoli: ['fine-linea'], attribuito: false },
    'pianificazione.vedi': { ruoli: ['produzione', 'magazzino'], attribuito: false },
    // Distribuire il lavoro non è del magazzino, che la plancia la legge per
    // sapere cosa arriva. Non attribuito: il nome che finisce sull'assegnazione
    // dice da dove viene la riga, non misura una persona — e una password a ogni
    // trascinamento, in una sessione dove se ne fanno venti, è l'attrito che fa
    // aggirare il sistema.
    'pianificazione.assegna': { ruoli: ['produzione'], attribuito: false },
    /*
     * L'anagrafica dei fallati (regola 8-bis). `ruoli: []` vuol dire il solo
     * amministratore, che può sempre: è un elenco d'ufficio, e il banco non ha
     * niente da farci — chi segnala il fallato lo sa già.
     *
     * **Nessuno dei due è attribuito**, ed è la stessa distinzione già presa per
     * `ricezione.fotografa-capo`. La presa in carico scrive un nome, ma il
     * criterio scritto in testa a questo file è «misura una persona, o parla a
     * nome sua a qualcuno»: qui non parte nessun timer, non si tocca nessun
     * costo, e nessuna mail esce a nome di chi clicca — l'unica mail di questa
     * storia parte al banco, sul gesto che è attribuito davvero. Il nome è
     * provenienza: dice chi risponde di quella segnalazione, e vale il login
     * della sessione. Una password per spegnere un avviso è precisamente
     * l'attrito che farebbe smettere di spegnerli, e l'elenco tornerebbe a
     * crescere all'infinito — che è il guasto da cui la regola 8-bis viene.
     */
    'fallati.vedi': { ruoli: [], attribuito: false },
    'fallati.prendi-in-carico': { ruoli: [], attribuito: false },
    /*
     * L'anagrafica degli scarti di lavorazione (regola 20-quater).
     *
     * `ruoli: ['produzione']` e non `[]` come i fallati, ed è la differenza fra
     * le due anagrafiche: il fallato lo si reclama al fornitore, e reclamare è
     * dell'ufficio; il capo rovinato in lavorazione **si rifà**, e a rifarlo è il
     * reparto. Mandare la produzione a chiedere il permesso all'ufficio per
     * dichiarare di aver rifatto un capo è l'attrito che fa reintegrare a voce —
     * e a quel punto l'elenco cresce senza che nessuno lo chiuda mai.
     *
     * Nessuno dei due attribuito, per il criterio scritto in testa a questo file:
     * il reintegro scrive un nome, ma non misura una persona e non parla a nome
     * sua a nessuno. Nessun timer, nessun costo, nessuna mail — e questa volta la
     * mail non esiste proprio, perché lo scarto misura noi.
     */
    'scarti.vedi': { ruoli: ['produzione'], attribuito: false },
    'scarti.reintegra': { ruoli: ['produzione'], attribuito: false },
    /*
     * La schermata delle giacenze.
     *
     * `['magazzino']` e non `[]` come i fallati: la domanda «quanti ne abbiamo»
     * la fa chi la merce ce l'ha davanti, non solo chi amministra. L'ufficio la
     * vede lo stesso, perché l'amministratore può sempre.
     *
     * Non attribuito, per il criterio scritto in testa a questo file: guardare
     * due colonne di numeri non misura nessuno e non parla a nome di nessuno.
     * È la stessa riga di `produzione.vedi`.
     *
     * Non c'è il gemello che scrive: nessuna delle due giacenze è nostra da
     * correggere — quella commerciale è di DecoNetwork, quella fisica del WMS —
     * e un gesto che le modificasse qui creerebbe una terza verità.
     */
    'giacenze.vedi': { ruoli: ['magazzino'], attribuito: false },
    // Whoever sees the stock prints its labels: they start from the same rows.
    'giacenze.stampa-etichette': { ruoli: ['magazzino'], attribuito: false },
    /*
     * The shop shelf. Unlike the stock screen this one has a writing twin: the
     * shelf is ours to count, so it is ours to correct.
     *
     * Loading is for whoever puts goods on the shelf — the warehouse from the
     * Modula, the bench straight from the supplier (rule 43). The physical count
     * is the warehouse's: it overrides the ledger, and the bench has no reason to.
     *
     * None attributed: the movement records the login as provenance, and no
     * timer, cost or mail hangs on it.
     */
    'scaffale.vedi': { ruoli: ['magazzino'], attribuito: false },
    'scaffale.carica': { ruoli: ['magazzino', 'ricezione'], attribuito: false },
    'scaffale.conta': { ruoli: ['magazzino'], attribuito: false },
    /*
     * Loading missions (rule 46): whoever loads the Modula plans what goes in.
     * Not attributed: the mission records its author, and nothing else hangs on it.
     */
    'missioni-carico.vedi': { ruoli: ['magazzino'], attribuito: false },
    'missioni-carico.scrivi': { ruoli: ['magazzino'], attribuito: false },
    /*
     * The PS130 weighings as the cloud stored them. Administrator only: the
     * screen answers «does the scale reach the cloud?», which is a check on the
     * installation, not anybody's daily job. Read-only — a weighing is a fact,
     * and the archive never rewrites one.
     */
    'misure.vedi': { ruoli: [], attribuito: false },
    /*
     * «Reparto dal vivo», the 3D floor. Administrator only to start with: it
     * shows every order and customer on the floor at once, and who else needs
     * that is a decision for later, not a default.
     */
    'reparto.vedi': { ruoli: [], attribuito: false },
    'admin.forza-parziale': {
        ruoli: [],
        attribuito: true,
        perche: "la deroga è nominativa e resta scritta sull'ordine fino alla spedizione (regola 20-ter)",
    },
    // Vedere l'elenco non crea né revoca niente: separato da `gestisci` perché
    // altrimenti aprire la schermata degli utenti chiederebbe la password, e una
    // lettura che chiede la password è una schermata che non si apre.
    'admin.vedi-utenti': { ruoli: [], attribuito: false },
    'admin.gestisci-utenti': { ruoli: [], attribuito: true, perche: 'crea e revoca identità' },
    // Le operatrici non sono identità: non danno accesso a niente e non firmano
    // nessun gesto. Cambiare le ore di un turno è manutenzione d'ufficio, e si fa
    // spesso — chiedere la password a ogni mezz'ora spostata è l'attrito che fa
    // tenere l'organico su un foglio a parte, che è il problema da cui si viene.
    'admin.vedi-operatrici': { ruoli: [], attribuito: false },
    'admin.gestisci-operatrici': { ruoli: [], attribuito: false },
    // Gli orari di lavoro, per la stessa ragione delle operatrici e con una in
    // più: si toccano due volte l'anno — l'ora legale, un venerdì corto — e
    // valgono per tutti, quindi non misurano nessuno e non firmano niente.
    //
    // Non c'è un `admin.vedi-turni` che gli faccia da coppia: il calendario esce
    // già dentro la vista della pianificazione, che chi apre quella schermata
    // può leggere. Un permesso di lettura in più sarebbe un secondo posto da
    // tenere allineato per una schermata che si apre lo stesso.
    'admin.gestisci-turni': { ruoli: [], attribuito: false },
    /*
     * Le impostazioni dell'ufficio. Non sono personali: valgono per tutti quelli
     * che entrano, e per questo le tocca solo chi amministra.
     *
     * Separate in lettura e scrittura come gli utenti, e per la stessa ragione:
     * una schermata che chiede la password per aprirsi è una schermata che non si
     * apre. Non attribuita nemmeno la scrittura — accendere un interruttore non
     * misura nessuno e non firma niente, e si fa due volte l'anno.
     */
    'impostazioni.vedi': { ruoli: [], attribuito: false },
    'impostazioni.scrivi': { ruoli: [], attribuito: false },
};
/**
 * L'amministratore può sempre.
 *
 * È una scelta dichiarata, non una svista: con cinque ruoli e un gruppo
 * piccolo, una matrice di eccezioni costerebbe più di quanto protegge. Se un
 * giorno servisse un amministratore che *non* può ricevere merce, questa è la
 * riga da cambiare — e sarà una decisione, non una scoperta.
 */
export function can(ruoli, gesto) {
    if (ruoli.includes('amministratore'))
        return true;
    return PERMESSI[gesto].ruoli.some((r) => ruoli.includes(r));
}
/** Il gesto scrive un nome: pretende un'identità confermata di recente. */
export function requiresConfirmation(gesto) {
    return PERMESSI[gesto].attribuito;
}
/** Perché quel gesto è attribuito. Vuoto quando non lo è. */
export function attributionReason(gesto) {
    return PERMESSI[gesto].perche ?? null;
}
/**
 * Chi vede una schermata: chi ha uno dei ruoli che quella chiede.
 *
 * Sta qui e non nelle due app perché è la stessa regola di `puo` applicata a un
 * elenco invece che a un gesto — «l'amministratore può sempre» compreso — e
 * perché era già scritta due volte, una al banco e una in ufficio, con firme
 * che avevano cominciato a divergere.
 *
 * Le due app restano due: `SCHERMATE` è di ciascuna, ed è giusto così. Comune è
 * il criterio, non l'elenco.
 */
export function seesScreen(ruoli, chieste) {
    if (ruoli.includes('amministratore'))
        return true;
    return chieste.some((r) => ruoli.includes(r));
}
/** Tutti i gesti che un insieme di ruoli può fare. Serve alle schermate. */
export function allowedActions(ruoli) {
    return Object.keys(PERMESSI).filter((g) => can(ruoli, g));
}
/**
 * La conferma d'identità è ancora valida?
 *
 * `confermataIl` è quando l'utente ha ridigitato la password. La finestra di
 * grazia esiste perché una raffica di aperture di lavorazione non deve chiedere
 * la password N volte di fila; è deliberatamente **breve**, perché è il tempo
 * in cui una postazione lasciata incustodita parla a nome di qualcun altro.
 */
export const CONFIRMATION_GRACE_MS = 5 * 60 * 1000;
export function confirmationValid(confermataIl, adesso, grazia = CONFIRMATION_GRACE_MS) {
    if (confermataIl === null)
        return false;
    if (confermataIl > adesso)
        return false; // conferma nel futuro: orologio storto
    return adesso - confermataIl <= grazia;
}
/**
 * La decisione completa su un gesto: prima il ruolo, poi la freschezza.
 *
 * L'ordine conta: a chi non ha il ruolo si dice che non ha il ruolo, non gli si
 * chiede la password per poi negargli il gesto lo stesso.
 */
export function authorize(ruoli, gesto, confermataIl, adesso, grazia = CONFIRMATION_GRACE_MS) {
    if (!can(ruoli, gesto)) {
        return {
            consentito: false,
            motivo: 'ruolo',
            messaggio: `Il gesto «${gesto}» non è fra quelli del tuo ruolo.`,
        };
    }
    if (requiresConfirmation(gesto) && !confirmationValid(confermataIl, adesso, grazia)) {
        return {
            consentito: false,
            motivo: 'conferma',
            messaggio: `Il gesto «${gesto}» scrive il tuo nome: ${attributionReason(gesto)}. ` +
                'Ridigita la password per confermare che sei tu.',
        };
    }
    return { consentito: true };
}
//# sourceMappingURL=permissions.js.map